import { useEffect } from 'react';

import { Footer } from '../components/Footer';
import { usePageMeta } from '../hooks/usePageMeta';
import { getPublicPageMeta } from '../seo/publicPageMeta';
import { trackPageView } from '../services/telemetry';

const linkClass = 'text-orange-600 underline hover:text-blue-800';

interface PolicySectionProps {
  sectionNumber: number;
  title: string;
  children: React.ReactNode;
}

function PolicySection({ sectionNumber, title, children }: PolicySectionProps) {
  return (
    <section
      id={`policy-section-${sectionNumber}`}
      className="scroll-mt-6"
      aria-labelledby={`policy-heading-${sectionNumber}`}
    >
      <div className="mb-4 flex items-center gap-3">
        <span
          className={`
            flex h-10 w-10 shrink-0 items-center justify-center rounded-full
            bg-yellow-400 text-lg font-bold text-gray-800
          `}
          aria-hidden="true"
        >
          {sectionNumber}
        </span>
        <h2
          id={`policy-heading-${sectionNumber}`}
          className="text-xl font-bold text-gray-800 sm:text-2xl"
        >
          {title}
        </h2>
      </div>
      <div className="ml-[52px] space-y-4 text-gray-600">{children}</div>
    </section>
  );
}

function PrivacyPolicy() {
  usePageMeta(getPublicPageMeta('/privacy-policy'));

  useEffect(() => {
    trackPageView('PrivacyPolicy', '/privacy-policy');
  }, []);

  return (
    <>
      <main
        className={`
          bg-gray-25 flex min-h-screen flex-col items-center p-4 font-[Outfit]
        `}
      >
        <header className="mb-10 w-full max-w-6xl text-center">
          <p className="mb-2 text-sm font-medium text-yellow-600">
            PineappleMusicLab
          </p>
          <h1
            className={`
              mb-3 text-3xl font-bold text-gray-800
              sm:text-4xl
              lg:text-5xl
            `}
          >
            Privacy Policy
          </h1>
          <p className="text-base text-gray-600 sm:text-lg">
            Effective Date: May 2, 2026 &nbsp;|&nbsp; Last Updated: May 2, 2026
          </p>
        </header>

        <article className="mb-12 flex w-full max-w-6xl flex-col gap-12">
          <div className="space-y-4 text-gray-600">
            <p>
              PineappleMusicLab (&quot;we,&quot; &quot;us,&quot; or
              &quot;our&quot;) operates the PineappleMusicLab website and mobile
              application (collectively, the &quot;Service&quot;). This Privacy
              Policy explains how we collect, use, store, and share your
              information when you use our Service.
            </p>
            <p>
              By using PineappleMusicLab, you agree to the collection and use of
              information as described in this policy. If you do not agree,
              please do not use our Service.
            </p>
          </div>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <PolicySection sectionNumber={1} title="Information We Collect">
            <h3 className="text-base font-semibold text-gray-800">
              Information You Provide
            </h3>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong className="text-gray-800">Account information:</strong>{' '}
                Your chosen username, email address, and password, collected
                during registration through our authentication provider. A legal
                name is not required to create an account.
              </li>
              <li>
                <strong className="text-gray-800">Uploaded content:</strong>{' '}
                Sheet music files you upload to the Service, including MusicXML
                files, PDFs, and images.
              </li>
              <li>
                <strong className="text-gray-800">Payment information:</strong>{' '}
                Billing details such as your name, payment card number, and
                billing address, collected and processed by our third-party
                payment processor. We do not store your full card details on our
                servers.
              </li>
            </ul>

            <h3 className="mt-6 text-base font-semibold text-gray-800">
              Information Collected Automatically
            </h3>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong className="text-gray-800">Usage analytics:</strong>{' '}
                Information about how you interact with the Service, including
                pages visited, features used, session duration, and approximate
                location (derived from IP address).
              </li>
              <li>
                <strong className="text-gray-800">Device information:</strong>{' '}
                Device type, operating system, browser type, screen resolution,
                and unique device identifiers.
              </li>
              <li>
                <strong className="text-gray-800">
                  Crash logs and diagnostics:
                </strong>{' '}
                Error reports and performance data to help us identify and fix
                issues.
              </li>
              <li>
                <strong className="text-gray-800">
                  Cookies and similar technologies:
                </strong>{' '}
                We use cookies and similar tracking technologies on our website
                to maintain your session, remember preferences, and collect
                analytics data. See Section 7 for details.
              </li>
            </ul>
          </PolicySection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <PolicySection sectionNumber={2} title="How We Use Your Information">
            <p>We use the information we collect for the following purposes:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong className="text-gray-800">
                  Providing the Service:
                </strong>{' '}
                To authenticate your account, process your uploaded sheet music,
                generate AI-powered piano fingerings and practice-assisting
                annotations, and deliver the results to you.
              </li>
              <li>
                <strong className="text-gray-800">
                  Billing and subscriptions:
                </strong>{' '}
                To process payments, manage your subscription, and send
                transaction-related communications.
              </li>
              <li>
                <strong className="text-gray-800">
                  Improving our AI models:
                </strong>{' '}
                Selected user-uploaded sheet music may be used to train and
                improve our proprietary AI models, including those used for
                fingering generation, music recognition, and image processing.
                We may use your uploaded content for this purpose unless you opt
                out by contacting us.
              </li>
              <li>
                <strong className="text-gray-800">
                  Analytics and product improvement:
                </strong>{' '}
                To understand how the Service is used, diagnose technical
                issues, and improve performance and features.
              </li>
              <li>
                <strong className="text-gray-800">Communication:</strong> To
                respond to your inquiries, send service-related notices, and
                notify you of policy changes.
              </li>
            </ul>

            <div
              className={`
                mt-4 rounded-r-lg border-l-4 border-yellow-400 bg-yellow-50 p-5
                text-gray-800 shadow-sm
              `}
            >
              <p className="mb-0 text-sm sm:text-base">
                <strong>About our AI:</strong> PineappleMusicLab uses
                proprietary, purpose-built AI models that run on our own
                infrastructure. Your personal data is not sent to any
                third-party AI provider.
              </p>
            </div>
          </PolicySection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <PolicySection
            sectionNumber={3}
            title="How We Share Your Information"
          >
            <p>
              We do not sell your personal information. We share data only with
              the following categories of service providers, each of whom is
              contractually obligated to protect your data:
            </p>

            <div
              className={`
                overflow-x-auto rounded-xl border border-gray-200 bg-white
                shadow-sm
              `}
            >
              <table
                className={`
                  w-full min-w-[520px] border-collapse text-left text-sm
                `}
              >
                <thead>
                  <tr className="border-b-2 border-gray-200 bg-yellow-50">
                    <th className="px-4 py-3 font-semibold text-gray-800">
                      Category
                    </th>
                    <th className="px-4 py-3 font-semibold text-gray-800">
                      Purpose
                    </th>
                    <th className="px-4 py-3 font-semibold text-gray-800">
                      Data Shared
                    </th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-200">
                    <td className="px-4 py-3 align-top">
                      Authentication provider
                    </td>
                    <td className="px-4 py-3 align-top">
                      User sign-up and login
                    </td>
                    <td className="px-4 py-3 align-top">
                      Username, email, password (hashed)
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-4 py-3 align-top">Payment processor</td>
                    <td className="px-4 py-3 align-top">
                      Subscription billing
                    </td>
                    <td className="px-4 py-3 align-top">
                      Billing name, payment card details, transaction data
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-4 py-3 align-top">
                      Analytics and monitoring provider
                    </td>
                    <td className="px-4 py-3 align-top">
                      Usage analytics, crash reporting, performance monitoring
                    </td>
                    <td className="px-4 py-3 align-top">
                      Usage data, device info, crash logs, approximate location
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-4 py-3 align-top">Apple (App Store)</td>
                    <td className="px-4 py-3 align-top">
                      In-app purchases and subscriptions (iOS)
                    </td>
                    <td className="px-4 py-3 align-top">
                      Transaction and subscription data
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 align-top">
                      Google (Google Play)
                    </td>
                    <td className="px-4 py-3 align-top">
                      In-app purchases and subscriptions (Android)
                    </td>
                    <td className="px-4 py-3 align-top">
                      Transaction and subscription data
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              A current list of our key sub-processors (for example, Stripe for
              payment processing and Microsoft Azure AD B2C for authentication)
              is available on request by contacting us at{' '}
              <a
                href="mailto:hello@pineapplemusiclab.com"
                className={linkClass}
              >
                hello@pineapplemusiclab.com
              </a>
              .
            </p>
            <p>
              We may also disclose your information if required by law, to
              enforce our Terms of Service, or to protect the rights, safety, or
              property of PineappleMusicLab or others.
            </p>
          </PolicySection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <PolicySection sectionNumber={4} title="Data Storage and Security">
            <p>
              Your data is stored using secure, industry-standard cloud
              infrastructure:
            </p>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong className="text-gray-800">Database services:</strong>{' '}
                User account data, project data, and uploaded content metadata.
              </li>
              <li>
                <strong className="text-gray-800">Object storage:</strong>{' '}
                Uploaded sheet music files (MusicXML, PDFs, and images) and
                generated outputs such as annotated sheet music.
              </li>
              <li>
                <strong className="text-gray-800">Payment processor:</strong>{' '}
                Payment and billing information (managed entirely within our
                processor&apos;s PCI-compliant infrastructure).
              </li>
              <li>
                <strong className="text-gray-800">
                  Authentication service:
                </strong>{' '}
                Login credentials (passwords are hashed and never stored in
                plain text).
              </li>
            </ul>
            <p>
              We implement industry-standard security measures, including
              encryption in transit (TLS/SSL) and access controls, to protect
              your information. However, no method of electronic transmission or
              storage is completely secure, and we cannot guarantee absolute
              security.
            </p>
          </PolicySection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <PolicySection sectionNumber={5} title="Data Retention">
            <p>
              We retain your personal information for as long as your account is
              active or as needed to provide the Service. Specifically:
            </p>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong className="text-gray-800">
                  Account and profile data:
                </strong>{' '}
                Retained until you delete your account.
              </li>
              <li>
                <strong className="text-gray-800">Uploaded content:</strong>{' '}
                Retained until you delete individual projects or your account.
              </li>
              <li>
                <strong className="text-gray-800">Payment records:</strong>{' '}
                Retained as required by applicable financial and tax
                regulations.
              </li>
              <li>
                <strong className="text-gray-800">
                  Analytics and crash data:
                </strong>{' '}
                Retained in accordance with our analytics provider&apos;s
                default retention policies.
              </li>
            </ul>
            <p>
              When you delete your account, we will delete your personal data
              from our systems. Please note that certain records (such as
              payment transaction history) may be retained as required by
              applicable financial and tax laws.
            </p>
          </PolicySection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <PolicySection sectionNumber={6} title="Your Rights and Choices">
            <p>
              Depending on your location, you may have the following rights
              regarding your personal data:
            </p>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong className="text-gray-800">Access:</strong> Request a
                copy of the personal data we hold about you.
              </li>
              <li>
                <strong className="text-gray-800">Correction:</strong> Request
                that we correct inaccurate or incomplete data.
              </li>
              <li>
                <strong className="text-gray-800">Deletion:</strong> Delete
                individual projects within the app, or request full account
                deletion. The app includes an in-app account deletion button for
                your convenience.
              </li>
              <li>
                <strong className="text-gray-800">
                  Opt out of AI training:
                </strong>{' '}
                Contact us to opt out of having your uploaded content used for
                AI model improvement.
              </li>
              <li>
                <strong className="text-gray-800">Withdraw consent:</strong>{' '}
                Where we rely on your consent, you may withdraw it at any time
                by contacting us.
              </li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a
                href="mailto:hello@pineapplemusiclab.com"
                className={linkClass}
              >
                hello@pineapplemusiclab.com
              </a>
              .
            </p>
          </PolicySection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <PolicySection
            sectionNumber={7}
            title="Cookies and Tracking Technologies"
          >
            <p>
              Our website uses cookies and similar technologies for the
              following purposes:
            </p>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong className="text-gray-800">Essential cookies:</strong>{' '}
                Required for authentication and core functionality.
              </li>
              <li>
                <strong className="text-gray-800">Analytics cookies:</strong>{' '}
                Used by our analytics provider to collect usage data and improve
                the Service.
              </li>
            </ul>
            <p>
              You can manage cookie preferences through your browser settings.
              Disabling essential cookies may prevent you from using certain
              features of the Service.
            </p>
          </PolicySection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <PolicySection sectionNumber={8} title="Children's Privacy">
            <p>
              PineappleMusicLab is intended for users aged 13 and older.
              Accounts may not be registered to anyone under 13, and we do not
              knowingly collect personal information from anyone under 13. A
              parent or legal guardian may use their own account to allow a
              younger family member to practice with the Service, but the
              account holder remains the registered user and is responsible for
              all activity on the account.
            </p>
            <p>
              If you are between 13 and 18, you should use the Service only with
              the involvement of a parent or legal guardian.
            </p>
            <p>
              If we learn that we have registered an account to a child under
              13, we will take steps to delete the account and any associated
              personal information promptly. If you believe a child under 13 has
              provided us personal information, please contact us at{' '}
              <a
                href="mailto:hello@pineapplemusiclab.com"
                className={linkClass}
              >
                hello@pineapplemusiclab.com
              </a>
              .
            </p>
          </PolicySection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <PolicySection sectionNumber={9} title="International Data Transfers">
            <p>
              Your information may be transferred to and processed in countries
              other than your own, including Canada and the United States, where
              our servers and service providers are located. By using the
              Service, you consent to such transfers. We take steps to ensure
              that your data receives an adequate level of protection in
              accordance with applicable laws.
            </p>
          </PolicySection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <PolicySection
            sectionNumber={10}
            title="Changes to This Privacy Policy"
          >
            <p>
              We may update this Privacy Policy from time to time. When we make
              material changes, we will notify you by email (sent to the address
              associated with your account) and update the &quot;Last
              Updated&quot; date at the top of this page. Your continued use of
              the Service after changes become effective constitutes your
              acceptance of the updated policy.
            </p>
          </PolicySection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <PolicySection sectionNumber={11} title="Contact Us">
            <p>
              If you have any questions or concerns about this Privacy Policy or
              our data practices, please contact us:
            </p>
            <p className="text-gray-800">
              <strong>PineappleMusicLab</strong>
              <br />
              Email:{' '}
              <a
                href="mailto:hello@pineapplemusiclab.com"
                className={linkClass}
              >
                hello@pineapplemusiclab.com
              </a>
            </p>
          </PolicySection>
        </article>
      </main>

      <Footer />
    </>
  );
}

export { PrivacyPolicy };
